import { MDXProvider } from '@mdx-js/react';

import { Link } from '@/core/i18n/navigation';
import { contentDate } from '@/lib/content-date';
import {
  getChildren,
  getContent,
  getSources,
  relatedPages,
  type ContentPage,
} from '@/lib/content-registry';
import { pageCrumbs, pageSchema } from '@/lib/seo';
import { m } from '@/paraglide/messages.js';
import {
  ComparisonTable,
  GuideCard,
  GuideLayout,
  PuzzleDiagram,
  QuickAnswer,
  RelatedGuides,
  SourceList,
} from '@/components/control-resonant/guide-layout';
import { StructuredData } from '@/components/control-resonant/structured-data';

import { Footer } from './footer';
import { Header } from './header';

export function ContentBlock({
  page,
  tool,
}: {
  page: ContentPage;
  tool?: React.ReactNode;
}) {
  const content = getContent(page.url);
  const Content = content?.default;
  const crumbs = pageCrumbs(page);
  return (
    <>
      <Header />
      <GuideLayout
        page={page}
        crumbs={crumbs}
        breadcrumbLabel={m['control.breadcrumb']()}
        updatedLabel={m['control.updated']()}
        dateLabel={contentDate(page.updatedAt, page.locale || 'en')}
        wide={page.type === 'hub' || page.type === 'tool'}
      >
        <StructuredData data={pageSchema(page, crumbs)} />
        {content && (
          <QuickAnswer label={m['control.quick_answer']()}>
            {content.quickAnswer}
          </QuickAnswer>
        )}
        {content?.diagram && <PuzzleDiagram {...content.diagram} />}
        {tool}
        {page.type === 'hub' && (
          <div className="crt-grid crt-grid-three">
            {getChildren(page.url).map((child) => (
              <GuideCard
                key={child.url}
                page={child}
                answer={getContent(child.url)?.quickAnswer}
              />
            ))}
          </div>
        )}
        {content?.table && <ComparisonTable {...content.table} />}
        {Content && (
          <div className="crt-prose">
            <MDXProvider
              components={{
                a: ({
                  href = '',
                  children,
                  ...props
                }: React.ComponentProps<'a'>) => (
                  <Link href={href} {...props}>
                    {children}
                  </Link>
                ),
              }}
            >
              <Content />
            </MDXProvider>
          </div>
        )}
        <RelatedGuides
          title={m['control.related']()}
          pages={relatedPages(page, content?.related)}
        />
        {!!content?.sourceIds?.length && (
          <SourceList
            title={m['control.sources']()}
            verifiedLabel={m['control.verified']()}
            date={page.updatedAt}
            dateLabel={contentDate(page.updatedAt, page.locale || 'en')}
            sources={getSources(content.sourceIds)}
          />
        )}
      </GuideLayout>
      <Footer />
    </>
  );
}
